import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Brain, Download, Eye, Search, Users, TrendingUp, BarChart3, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const TRAIT_COLORS = {
  'Openness': '#8B5CF6',
  'Conscientiousness': '#3B82F6',
  'Extraversion': '#F59E0B',
  'Agreeableness': '#10B981',
  'Emotional Stability': '#EC4899',
  'Leadership': '#6366F1',
  'Teamwork': '#14B8A6',
  'Integrity': '#8B5CF6',
  'Stress Tolerance': '#EF4444',
  'Decision Making': '#F97316'
};

// Grade calculation based on score
const getGrade = (score) => {
  if (score >= 90) return { grade: 'A', color: 'bg-green-600 text-white', label: 'A Grade' };
  if (score >= 80) return { grade: 'B', color: 'bg-blue-600 text-white', label: 'B Grade' };
  if (score >= 70) return { grade: 'C', color: 'bg-teal-500 text-white', label: 'C Grade' };
  if (score >= 60) return { grade: 'D', color: 'bg-yellow-500 text-white', label: 'D Grade' };
  if (score >= 50) return { grade: 'E', color: 'bg-orange-500 text-white', label: 'E Grade' };
  return { grade: 'F', color: 'bg-red-600 text-white', label: 'Fail' };
};

function PsychometricAdmin() {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState('all');
  const [attempts, setAttempts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchTests();
    fetchBatches();
  }, []);

  useEffect(() => {
    if (selectedTest) {
      fetchAttempts(selectedTest);
      fetchSummary(selectedTest);
    }
  }, [selectedTest]);

  const fetchTests = async () => {
    try {
      const response = await api.get('/tests');
      // Filter tests with psychometric component
      const psychometricTests = response.data.filter(t => t.has_psychometric_test);
      setTests(psychometricTests);
      if (psychometricTests.length > 0) {
        setSelectedTest(psychometricTests[0].id);
      }
    } catch (error) {
      console.error('Error fetching tests:', error);
      toast.error('Failed to load tests');
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await api.get('/batches');
      setBatches(response.data);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchAttempts = async (testId) => {
    try {
      const response = await api.get(`/admin/psychometric/attempts?test_id=${testId}`);
      setAttempts(response.data);
    } catch (error) {
      console.error('Error fetching attempts:', error);
    }
  };

  const fetchSummary = async (testId) => {
    try {
      const response = await api.get(`/admin/psychometric/test/${testId}/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const handleToggleVisibility = async (testId, visible) => {
    try {
      await api.put(`/admin/psychometric/visibility/${testId}?visible=${visible}`);
      toast.success(`Results visibility ${visible ? 'enabled' : 'disabled'} for students`);
      fetchTests();
    } catch (error) {
      console.error('Error updating visibility:', error);
      toast.error('Failed to update visibility');
    }
  };

  const downloadFile = (base64Data, filename, mimeType) => {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportExcel = async () => {
    if (!selectedTest) return;
    setExporting(true);
    try {
      const batchParam = selectedBatch !== 'all' ? `&batch_id=${selectedBatch}` : '';
      const response = await api.get(`/admin/psychometric/export/${selectedTest}?format=excel${batchParam}`);
      downloadFile(response.data.data, response.data.filename, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      toast.success('Excel file downloaded successfully');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export results');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    if (!selectedTest) return;
    setExporting(true);
    try {
      const batchParam = selectedBatch !== 'all' ? `&batch_id=${selectedBatch}` : '';
      const response = await api.get(`/admin/psychometric/export/${selectedTest}?format=pdf${batchParam}`);
      downloadFile(response.data.data, response.data.filename, 'application/pdf');
      toast.success('PDF file downloaded successfully');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBadge = (score) => {
    if (score >= 80) return { bg: 'bg-green-100', text: 'text-green-700', label: 'Excellent' };
    if (score >= 60) return { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Strong' };
    if (score >= 40) return { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Moderate' };
    return { bg: 'bg-red-100', text: 'text-red-700', label: 'Needs Improvement' };
  };

  // Filter attempts by batch and search query
  const filteredAttempts = attempts.filter(a => {
    const matchesSearch = a.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          a.user_email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesBatch = selectedBatch === 'all' || a.batch_id === selectedBatch;
    return matchesSearch && matchesBatch;
  });

  const currentTest = tests.find(t => t.id === selectedTest);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Brain className="w-12 h-12 text-purple-600 animate-pulse" />
        </div>
      </Layout>
    );
  }

  if (tests.length === 0) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <Brain className="w-8 h-8 text-purple-600" />
            <h1 className="text-3xl font-bold text-gray-900">Psychometric Results</h1>
          </div>
          
          <Card className="p-8 text-center">
            <Brain className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">No Psychometric Tests Found</h2>
            <p className="text-gray-500 mb-4">
              Create a test with the psychometric assessment option enabled to view results here.
            </p>
            <Button onClick={() => navigate('/admin/tests/create')} className="bg-purple-600 hover:bg-purple-700">
              Create Test
            </Button>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Brain className="w-8 h-8 text-purple-600" />
            <h1 className="text-3xl font-bold text-gray-900" data-testid="psychometric-admin-title">
              Psychometric Results
            </h1>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={selectedTest || ''} onValueChange={setSelectedTest}>
              <SelectTrigger className="w-56" data-testid="test-selector">
                <SelectValue placeholder="Select a test" />
              </SelectTrigger>
              <SelectContent>
                {tests.map(test => (
                  <SelectItem key={test.id} value={test.id}>{test.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedBatch} onValueChange={setSelectedBatch}>
              <SelectTrigger className="w-48" data-testid="batch-selector">
                <SelectValue placeholder="All Batches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Batches</SelectItem>
                {batches.map(batch => (
                  <SelectItem key={batch.id} value={batch.id}>{batch.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button onClick={handleExportExcel} variant="outline" disabled={exporting} data-testid="export-excel-btn">
              <Download className="w-4 h-4 mr-2" />
              Excel
            </Button>
            
            <Button onClick={handleExportPDF} variant="outline" disabled={exporting} data-testid="export-pdf-btn">
              <FileText className="w-4 h-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Attempts</p>
                  <p className="text-2xl font-bold text-gray-900">{filteredAttempts.length}</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Average Score</p>
                  <p className="text-2xl font-bold text-gray-900">{summary.average_overall?.toFixed(1)}%</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Excellent/Strong</p>
                  <p className="text-2xl font-bold text-green-600">
                    {(summary.score_distribution?.excellent || 0) + (summary.score_distribution?.strong || 0)}
                  </p>
                </div>
              </div>
            </Card>
            
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Results Visible</p>
                  <p className="text-sm font-medium text-gray-700">to Students</p>
                </div>
                <Switch
                  checked={currentTest?.psychometric_results_visible || false}
                  onCheckedChange={(checked) => handleToggleVisibility(selectedTest, checked)}
                  data-testid="visibility-toggle"
                />
              </div>
            </Card>
          </div>
        )}

        {/* Grade Legend */}
        <Card className="p-4 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Grade Scale</h3>
          <div className="flex flex-wrap gap-3">
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-600 text-white">A (90%+)</span>
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-600 text-white">B (80-89%)</span>
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-teal-500 text-white">C (70-79%)</span>
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-yellow-500 text-white">D (60-69%)</span>
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-orange-500 text-white">E (50-59%)</span>
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-600 text-white">Fail (&lt;50%)</span>
          </div>
        </Card>

        {/* Average Trait Scores Chart */}
        {summary && summary.average_scores && Object.keys(summary.average_scores).length > 0 && (
          <Card className="p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Average Trait Scores</h2>
            <div className="space-y-3">
              {Object.entries(summary.average_scores).map(([trait, score]) => (
                <div key={trait} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-700">{trait}</span>
                    <span className={`font-bold ${getScoreColor(score)}`}>{score?.toFixed(1)}%</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ 
                        width: `${score}%`,
                        backgroundColor: TRAIT_COLORS[trait] || '#8B5CF6'
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Search and Results Table */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Individual Results</h2>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="results-table">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Student</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-700">Overall Score</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-700">Grade</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-700">Level</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-700">Completed</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAttempts.map((attempt) => {
                  const badge = getScoreBadge(attempt.overall_score);
                  const grade = getGrade(attempt.overall_score);
                  return (
                    <tr key={attempt.id} className="border-b hover:bg-gray-50" data-testid={`attempt-row-${attempt.id}`}>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-gray-900">{attempt.user_name}</p>
                          <p className="text-sm text-gray-500">{attempt.user_email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`text-lg font-bold ${getScoreColor(attempt.overall_score)}`}>
                          {attempt.overall_score?.toFixed(1)}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-3 py-1 rounded-full text-sm font-bold ${grade.color}`}>
                          {grade.grade}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center text-sm text-gray-500">
                        {attempt.end_time ? new Date(attempt.end_time).toLocaleDateString() : 'In Progress'}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/psychometric/detail/${attempt.id}`)}
                          data-testid={`view-detail-${attempt.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View Detail
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {filteredAttempts.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                {searchQuery ? 'No results found for your search.' : 'No psychometric submissions yet.'}
              </div>
            )}
          </div>
        </Card>
      </div>
    </Layout>
  );
}

export default PsychometricAdmin;
